import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { ssoEnabled } from '../auth/authConfig';
import { useAuth } from '../hooks/useAuth';
import { GCC_STUDIO_API_BASE_URL } from '../runtimeConfig';
import { gensearchInsights, listGensearchAgents } from '../services/alphasenseService';
import { encryptPayload } from '../utils/cryptoClient';
// import { searchAlphaSenseDocuments } from '../services/alphasenseService'; // replaced by gensearchInsights
// import { ALPHASENSE_FEED_KEYWORD } from '../constants/alphasense'; // replaced by ALPHASENSE_FEED_PROMPT

const REFRESH_MS = 25 * 60 * 1000; // 25 min — 5 min before the 30-min expiry

const KNOWN_ROUTES = new Set([
  '/', '/gcc-forums', '/why-accenture', '/client-stories',
  '/gcc-health-check', '/clientstories', '/insight-page', '/industry-narratives',
]);
const KNOWN_PREFIXES = ['/offering/'];

function isKnownRoute(pathname) {
  return KNOWN_ROUTES.has(pathname) || KNOWN_PREFIXES.some(p => pathname.startsWith(p));
}

const AlphaSenseTokenContext = createContext({
  alphasenseToken: null, // actor token — for document search, suggestions, gensearch list
  oboToken: null,        // OBO token  — for runWorkflowAgent, deepResearch
  isTokenReady: false,
  feedItems: [],
  feedLoaded: false,
  feedError: null,
  agents: [],
  agentsLoading: true,
  agentsError: null,
});

export function useAlphaSenseToken() {
  return useContext(AlphaSenseTokenContext);
}

/**
 * Provides a live AlphaSense access token to the component tree.
 *
 * When SSO is disabled this is a no-op passthrough (no MSAL hooks run).
 * When SSO is enabled the token is fetched from the AlphaSense proxy
 * immediately after MSAL sign-in and silently refreshed every 25 minutes.
 */
export function AlphaSenseTokenProvider({ children }) {
  if (!ssoEnabled) {
    return (
      <AlphaSenseTokenContext.Provider value={{
        alphasenseToken: null,
        oboToken: null,
        isTokenReady: false,
        feedItems: [],
        feedLoaded: true,
        feedError: null,
        agents: [],
        agentsLoading: false,
        agentsError: null,
      }}>
        {children}
      </AlphaSenseTokenContext.Provider>
    );
  }
  return <AlphaSenseTokenFetcher>{children}</AlphaSenseTokenFetcher>;
}


// const mapDocument = (doc) => {
//   const companies = doc.companies?.map((c) => c.name).filter(Boolean).join(', ');
//   const meta = ['AlphaSense', companies, doc.releasedDay].filter(Boolean).join(' · ');
//   return { title: doc.title, meta, url: doc.documentUrl };
// };

const mapHeadline = (item) => ({
  title: item.headline,
  meta: [item.category, item.releaseDate].filter(Boolean).join(' · '),
  url: item.docURL,
  summary: item.summary,
});

const is401 = (err) => err?.message?.includes('401');

function AlphaSenseTokenFetcher({ children }) {
  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();
  const { pathname } = useLocation();
  const { getApiToken } = useAuth();
  const userEmail = (instance.getActiveAccount()?.username ?? '')
    .replace('@ds.dev.accenture.com', '@accenture.com');
  const [alphasenseToken, setAlphasenseToken] = useState(null); // actor token
  const [oboToken, setOboToken] = useState(null);               // OBO token
  const [feedItems, setFeedItems] = useState([]);
  const [feedLoaded, setFeedLoaded] = useState(false);
  const [feedError, setFeedError] = useState(null);
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [agentsError, setAgentsError] = useState(null);
  const timerRef = useRef(null);
  const hasFetchedRef = useRef(false);
  const hasFetchedInsightsRef = useRef(false); // insights are expensive — fetch only once per session

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // actorToken is used for gensearch insights and list (GQL API audience)
  const prefetchData = (msalToken, actorToken) => {
    // searchAlphaSenseDocuments(ALPHASENSE_FEED_KEYWORD, msalToken, actorToken)
    //   .then((data) => {
    //     setFeedItems((data?.data?.documents ?? []).map(mapDocument));
    //     setFeedError(null);
    //   })
    //   .catch((err) => {
    //     setFeedError(
    //       is401(err)
    //         ? '401 Unauthorized — AlphaSense token expired.'
    //         : 'Failed to load feed.'
    //     );
    //   })
    //   .finally(() => setFeedLoaded(true));

    if (!hasFetchedInsightsRef.current) {
      hasFetchedInsightsRef.current = true;
      gensearchInsights(msalToken, actorToken)
        .then((data) => {
          setFeedItems((data?.headlines ?? []).map(mapHeadline));
          setFeedError(null);
        })
        .catch((err) => {
          hasFetchedInsightsRef.current = false; // allow retry on next token refresh if it failed
          setFeedError(
            is401(err)
              ? '401 Unauthorized — AlphaSense token expired.'
              : 'Failed to load feed.'
          );
        })
        .finally(() => setFeedLoaded(true));
    }

    listGensearchAgents(msalToken, actorToken)
      .then((data) => {
        setAgents(data?.agents ?? []);
        setAgentsError(null);
      })
      .catch((err) => {
        setAgentsError(
          is401(err)
            ? '401 Unauthorized — AlphaSense token expired.'
            : 'Failed to load agents.'
        );
      })
      .finally(() => setAgentsLoading(false));
  };

  const fetchToken = async () => {
    try {
      const msalToken = await getApiToken();

      // Step 1: actor token (aud: api) — hard fail; without this nothing works
      const tokenRes = await fetch(`${GCC_STUDIO_API_BASE_URL}/token`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${msalToken}`,
        },
      });
      if (!tokenRes.ok) throw new Error(`AlphaSense /token responded ${tokenRes.status}`);
      const { access_token } = await tokenRes.json();

      // Step 2: OBO token (aud: m2m/on-behalf-of) — soft fail; search/list still works
      // Use expiresIn from response to drive the refresh interval dynamically.
      let fetchedOboToken = null;
      let nextRefreshMs = REFRESH_MS; // fallback: 25 min
      try {
        const { value, cryptoKeyDetails } = await encryptPayload({ userQuery: userEmail });
        const oboRes = await fetch(`${GCC_STUDIO_API_BASE_URL}/utility/obo/token/by-user`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${access_token}`, // AlphaSense actor token, not MSAL
            'x-cryptokey-details': cryptoKeyDetails,
          },
          body: JSON.stringify({ value }),
        });
        if (!oboRes.ok) throw new Error(`OBO token exchange responded ${oboRes.status}`);
        const oboData = await oboRes.json();
        fetchedOboToken = oboData.accessToken;
        // Refresh 5 min before the OBO token expires; minimum 1 min
        if (oboData.expiresIn) {
          nextRefreshMs = Math.max((oboData.expiresIn - 300) * 1000, 60_000);
        }
      } catch (oboErr) {
        console.error('[AlphaSenseToken] OBO exchange failed, retrying in 5 min:', oboErr);
        setOboToken(null); // clear stale token so callers don't use an expired one
        nextRefreshMs = 5 * 60_000; // retry OBO in 5 min; avoids hammering actor token endpoint
      }

      setAlphasenseToken(access_token);
      if (fetchedOboToken) setOboToken(fetchedOboToken);
      prefetchData(msalToken, access_token);

      clearTimer();
      timerRef.current = setTimeout(fetchToken, nextRefreshMs);
    } catch (err) {
      // Actor token fetch failed — clear stale tokens, surface error, retry in 1 min
      console.error('[AlphaSenseToken] token fetch failed:', err);
      setAlphasenseToken(null);
      setOboToken(null);
      setFeedLoaded(true);
      setFeedError('Failed to connect to AlphaSense.');
      setAgentsLoading(false);
      setAgentsError('Failed to load agents.');
      clearTimer();
      timerRef.current = setTimeout(fetchToken, 60_000);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setAlphasenseToken(null);
      setOboToken(null);
      setFeedItems([]);
      setFeedLoaded(false);
      setFeedError(null);
      setAgents([]);
      setAgentsLoading(true);
      setAgentsError(null);
      hasFetchedRef.current = false;
      hasFetchedInsightsRef.current = false;
      clearTimer();
      return;
    }

    if (!isKnownRoute(pathname)) return; // skip API calls on 404 pages
    if (hasFetchedRef.current) return;   // already fetching; timer handles refresh
    hasFetchedRef.current = true;
    fetchToken();
    return clearTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, pathname]);

  return (
    <AlphaSenseTokenContext.Provider value={{
      alphasenseToken,
      oboToken,
      isTokenReady: !!oboToken,
      feedItems,
      feedLoaded,
      feedError,
      agents,
      agentsLoading,
      agentsError,
    }}>
      {children}
    </AlphaSenseTokenContext.Provider>
  );
}
